import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MAX_PASSWORD_LENGTH,
  allRulesSatisfied,
  evaluatePasswordRules,
} from '@wineryxplorer/shared';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PasswordRequirements } from '../components/auth/PasswordRequirements';
import { trpc } from '../lib/trpc';
import { showToast } from '../utils/toast';

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [resetComplete, setResetComplete] = useState(false);

  // Get token and email from URL params
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const resetPasswordMutation = trpc.auth.resetPassword.useMutation();

  // Validate that we have required params
  useEffect(() => {
    if (!token || !email) {
      showToast('Invalid reset link. Please request a new password reset.', 'error');
      setTimeout(() => {
        navigate('/forgot-password');
      }, 3000);
    }
  }, [token, email, navigate]);

  const passwordRules = evaluatePasswordRules(newPassword);

  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length > MAX_PASSWORD_LENGTH) {
      setPasswordError(`Password must be ${MAX_PASSWORD_LENGTH} characters or fewer`);
      return false;
    }
    if (!allRulesSatisfied(evaluatePasswordRules(password))) {
      setPasswordError('Password does not meet all requirements');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (confirm: string): boolean => {
    if (!confirm) {
      setConfirmError('Please confirm your password');
      return false;
    }
    if (confirm !== newPassword) {
      setConfirmError('Passwords do not match');
      return false;
    }
    setConfirmError('');
    return true;
  };

  const handleSubmit = async () => {
    const isPasswordValid = validatePassword(newPassword);
    const isConfirmValid = validateConfirmPassword(confirmPassword);

    if (!isPasswordValid || !isConfirmValid) {
      return;
    }

    try {
      const result = await resetPasswordMutation.mutateAsync({
        email,
        token,
        newPassword,
      });

      showToast(result.message, 'success');
      setResetComplete(true);

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: unknown) {
      if (error instanceof Error) {
        showToast(error.message, 'error');
      } else {
        showToast('Failed to reset password', 'error');
      }
    }
  };

  // Success view after password is reset
  if (resetComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card variant="elevated" padding="lg">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Reset!</h2>
              <p className="text-gray-600 mb-4">
                Your password has been successfully reset. Redirecting you to login...
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Don't render form if missing required params
  if (!token || !email) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card variant="elevated" padding="lg">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Reset Link</h2>
              <p className="text-gray-600 mb-4">
                This password reset link is invalid or has expired. Redirecting you to request a new one...
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Default view: reset password form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New Password</h1>
          <p className="text-gray-600">
            Enter a new password for your account
          </p>
        </div>

        <Card variant="elevated" padding="lg">
          <div className="space-y-4">
            {/* Email display */}
            <div className="text-sm text-gray-600 mb-4">
              Resetting password for <strong>{email}</strong>
            </div>

            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordError('');
                // Clear confirm error if passwords now match
                if (confirmPassword && e.target.value === confirmPassword) {
                  setConfirmError('');
                }
              }}
              onBlur={() => validatePassword(newPassword)}
              placeholder="Create a strong password"
              error={passwordError}
              success={newPassword.length > 0 && allRulesSatisfied(passwordRules)}
              fullWidth
              required
              disabled={resetPasswordMutation.isPending}
            />

            <PasswordRequirements password={newPassword} rules={passwordRules} />

            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmError('');
              }}
              onBlur={() => validateConfirmPassword(confirmPassword)}
              placeholder="Re-enter your password"
              error={confirmError}
              fullWidth
              required
              disabled={resetPasswordMutation.isPending}
            />

            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleSubmit}
              loading={resetPasswordMutation.isPending}
              disabled={resetPasswordMutation.isPending}
            >
              Reset Password
            </Button>
          </div>

          {/* Security note */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              For your security, passwords are also checked against known data breaches.
            </p>
          </div>
        </Card>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};
